import * as reportDomain from "@/domain/report"
import { isIssueSummaryReport } from "@/domain/report"
import {
  DEFAULT_LONG_FORMAT,
  getDateInUserLocale
} from "@/helpers/date.helpers"
import { getVariablesFromCronExpression } from "@/pages/ReportsPage/helpers/cron.helpers"

import type {
  ReportDefinitionResponse,
  ReportJobResponse
} from "@/api/useReports.types"
import type { JobReport, ReportDefinition, TypeReport } from "@/domain/report"
import type { StatusTagProps } from "@/molecules/StatusTag"

export const getReportTypeKey = (reportType: TypeReport) => {
  return `reports.form.fields.reportType.${isIssueSummaryReport(reportType) ? "issue" : "scope"}.label`
}

export const getCreator = (values: JobReport | ReportDefinition) =>
  values?.createdBy?.displayName || ""

export const getReportDefinition = (response: ReportDefinitionResponse) => {
  const { data = [], ...rest } = response || {}

  return {
    data: (Array.isArray(data) ? data : [data]).map((values) => ({
      key: values.id,
      creator: getCreator(values),
      reportTypeKey: getReportTypeKey(values.reportType),
      frequency: getVariablesFromCronExpression(values.schedule),
      isAllResources: false,
      ...values
    })),
    ...rest
  }
}

export const getStatusValueByReportJobStatus = (
  record: ReportJobResponse["data"][number]
): { status: StatusTagProps["status"]; tKey: string } => {
  const prefix = "reports.warehouse.statuses."

  if (reportDomain.isReportJobRunning(record))
    return { status: "pending", tKey: `${prefix}pending` }

  if (reportDomain.isReportJobSuccess(record))
    return { status: "success", tKey: `${prefix}success` }

  if (reportDomain.isReportJobFailed(record))
    return { status: "failed", tKey: `${prefix}failed` }

  return { status: "neutral", tKey: `${prefix}neutral` }
}

export const getReportJob = (response: ReportJobResponse) => {
  const { data = [], ...rest } = response || {}

  return {
    data: data.map((record) => ({
      key: record.id,
      creator: getCreator(record),
      reportTypeKey: getReportTypeKey(record.reportType),
      generatedAt: record.endedAt
        ? getDateInUserLocale(record.endedAt, DEFAULT_LONG_FORMAT)
        : "",
      pdfDownloadURL: record.artifacts.find(
        (artifact) => artifact.artifactType === "ARTIFACT_TYPE_PDF"
      )?.downloadUrl,
      statusValue: getStatusValueByReportJobStatus(record),
      ...record
    })),
    ...rest
  }
}
